import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { flows } from '@repletoryreach/sdk-api';
import { Button, Card, Group, Stack, Text, Title } from '@repletoryreach/sdk-ui';
import { Select } from '@repletoryreach/sdk-ui/inputs';
import { Modal } from '@repletoryreach/sdk-ui/overlays';
import { openConfirmModal } from '@repletoryreach/sdk-ui/modals';
import { notifyError, notifySuccess } from '@repletoryreach/sdk-ui/notifications';
import { useBots, botName } from '../../../../lib/bots';
import { useT } from '../../../../i18n/I18nProvider';

/**
 * The two things that can be done TO a chatbot rather than in it: make it the
 * default, and delete it. Only shown once an organisation has more than one.
 *
 * Deleting never strands anything. A chatbot with flows or accounts is deleted
 * by naming the chatbot that takes them over, in the same step; the default
 * chatbot cannot be deleted at all, because it is what answers wherever no
 * chatbot was chosen.
 */
export function BotActions({ bot }: { bot: flows.Bot }) {
  const t = useT();
  const qc = useQueryClient();
  const nav = useNavigate();
  const { bots, setActive, defaultBot } = useBots();
  const fallback = t('settings.assistant.defaultName');
  const name = botName(bot, fallback);
  const others = bots.filter((b) => b.id !== bot.id);
  const inUse = bot.flow_count > 0 || bot.channels.length > 0;
  const [moving, setMoving] = useState(false);
  const [heir, setHeir] = useState('');

  const makeDefault = useMutation({
    mutationFn: () => flows.makeDefaultBot(bot.id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['bots'] });
      void qc.invalidateQueries({ queryKey: ['bot-profile'] });
      notifySuccess(t('bots.actions.madeDefault', { name }), t('settings.assistant.title'));
    },
    onError: (e) => notifyError(e instanceof Error ? e.message : t('common.unknownError'), t('settings.assistant.title'))
  });

  const remove = useMutation({
    mutationFn: (reassignTo?: string) => flows.deleteBot(bot.id, { reassignTo }),
    onSuccess: () => {
      if (defaultBot) setActive(defaultBot.id);
      void qc.invalidateQueries({ queryKey: ['bots'] });
      void qc.invalidateQueries({ queryKey: ['flows'] });
      notifySuccess(t('bots.actions.deleted', { name }), t('settings.assistant.title'));
      nav('/settings/assistant');
    },
    onError: (e) => notifyError(e instanceof Error ? e.message : t('common.unknownError'), t('settings.assistant.title'))
  });

  function askDelete() {
    if (inUse) { setHeir(others[0]?.id ?? ''); setMoving(true); return; }
    openConfirmModal({
      title: t('bots.actions.delete.title', { name }),
      children: <Text role="bodySmall">{t('bots.actions.delete.body')}</Text>,
      labels: { confirm: t('bots.actions.delete.action'), cancel: t('action.cancel') },
      confirmProps: { color: 'red' },
      onConfirm: () => remove.mutate(undefined)
    });
  }

  return (
    <Card withBorder>
      <Stack gap="md">
        <Title order={5}>{t('bots.actions.title')}</Title>
        <Group justify="space-between" align="center" wrap="wrap" gap="sm">
          <Text role="bodySmall" c="var(--rr-color-text-muted)" style={{ flex: 1, minWidth: 240 }}>
            {bot.is_default ? t('bots.actions.isDefault') : t('bots.actions.defaultHint')}
          </Text>
          {!bot.is_default && (
            <Button
              size="sm"
              variant="outline"
              loading={makeDefault.isPending}
              onClick={() => openConfirmModal({
                title: t('bots.actions.makeDefault.title', { name }),
                children: <Text role="bodySmall">{t('bots.actions.makeDefault.body', { name })}</Text>,
                labels: { confirm: t('bots.actions.makeDefault.action'), cancel: t('action.cancel') },
                onConfirm: () => makeDefault.mutate()
              })}
            >
              {t('bots.actions.makeDefault.action')}
            </Button>
          )}
        </Group>
        {!bot.is_default && (
          <Group justify="space-between" align="center" wrap="wrap" gap="sm">
            <Text role="bodySmall" c="var(--rr-color-text-muted)" style={{ flex: 1, minWidth: 240 }}>
              {inUse ? t('bots.actions.delete.hintInUse') : t('bots.actions.delete.hint')}
            </Text>
            <Button size="sm" variant="outline" color="red" loading={remove.isPending} onClick={askDelete}>
              {t('bots.actions.delete.action')}
            </Button>
          </Group>
        )}
      </Stack>

      {moving && (
        <Modal opened={moving} onClose={() => setMoving(false)} title={t('bots.actions.delete.title', { name })} size="md">
          <Stack gap="md">
            <Text role="bodySmall">
              {t('bots.actions.move.body', { name, flows: bot.flow_count, accounts: bot.channels.length })}
            </Text>
            <Select
              label={t('bots.actions.move.label')}
              description={t('bots.actions.move.hint')}
              value={heir}
              onChange={(e) => setHeir(e.currentTarget.value)}
              data={others.map((b) => ({ value: b.id, label: botName(b, fallback) }))}
            />
            <Group justify="flex-end" gap="sm">
              <Button variant="subtle" onClick={() => setMoving(false)}>{t('action.cancel')}</Button>
              <Button color="red" disabled={!heir} loading={remove.isPending} onClick={() => remove.mutate(heir)}>
                {t('bots.actions.move.action')}
              </Button>
            </Group>
          </Stack>
        </Modal>
      )}
    </Card>
  );
}
